import { useState } from "react";
import {
  Eye,
  EyeOff,
  Copy,
  Check,
  AlertTriangle,
  RefreshCw,
  ShieldAlert,
  X,
} from "lucide-react";

const PaymentKeys = () => {
  const [isResetting, setIsResetting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [merchantCode, setMerchantCode] = useState("");
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCloseWarningModal, setShowCloseWarningModal] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  const handleResetKey = () => {
    setShowConfirmModal(true);
  };

  const confirmReset = async () => {
    setShowConfirmModal(false);
    setIsResetting(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const merchantRef = localStorage.getItem("active");

      const response = await fetch(
        "https://vaspayapi.v2nportal.com/api/v1/dashboard/reset-payment-keys",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            merchantRef: merchantRef || "",
          },
        }
      );

      const data = await response.json();

      if (data && data.data) {
        setSecretKey(data.data.secretKey);
        setMerchantCode(data.data.merchantCode);
        setIsKeyVisible(true); // Show key in success modal
        setShowSuccessModal(true); // Show success modal
        setKeyCopied(false); // Reset copy state
      } else {
        setMessage("Failed to reset payment keys. Please try again.");
      }
    } catch (error) {
      console.error("Reset error:", error);
      setMessage("Failed to reset payment keys. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  const cancelReset = () => {
    setShowConfirmModal(false);
  };

  const closeSuccessModal = () => {
    if (!keyCopied) {
      // Show custom warning modal instead of browser confirm
      setShowCloseWarningModal(true);
      return;
    }
    setShowSuccessModal(false);
    setMessage("Payment keys reset successfully!");
  };

  const forceCloseSuccessModal = () => {
    setShowCloseWarningModal(false);
    setShowSuccessModal(false);
    setMessage("Payment keys reset successfully!");
  };

  const cancelCloseWarning = () => {
    setShowCloseWarningModal(false);
  };

  const toggleKeyVisibility = () => {
    setIsKeyVisible(!isKeyVisible);
    setCopySuccess(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(secretKey);
      setCopySuccess(true);
      setKeyCopied(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  const maskKey = (key: string) => {
    if (!key) return "";
    if (key.length <= 16) return "•".repeat(key.length);
    return (
      key.substring(0, 8) +
      "•".repeat(Math.min(key.length - 16, 20)) +
      key.substring(key.length - 8)
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-8 text-center border-b border-gray-200">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <RefreshCw className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Payment Keys Management
          </h1>
          <p className="text-gray-600 text-sm sm:text-base max-w-md mx-auto">
            Manage your payment authentication credentials securely
          </p>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8">
          {/* Current Keys Display */}
          <div className="mb-8">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                <span className="ml-3 text-gray-600">Loading keys...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Merchant Code */}
                {merchantCode && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Merchant Code
                    </label>
                    <div className="relative">
                      <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm text-gray-900 break-all">
                        {merchantCode}
                      </div>
                    </div>
                  </div>
                )}

                {/* Secret Key */}
                {secretKey && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Secret Key
                    </label>
                    <div className="relative">
                      <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm text-gray-900 pr-20">
                        <div className="break-all overflow-hidden">
                          {isKeyVisible ? secretKey : maskKey(secretKey)}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                        <button
                          onClick={toggleKeyVisibility}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all duration-200"
                          title={isKeyVisible ? "Hide key" : "Show key"}
                        >
                          {isKeyVisible ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={copyToClipboard}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all duration-200"
                          title="Copy to clipboard"
                        >
                          {copySuccess ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>

                      {/* Copy success message */}
                      {copySuccess && (
                        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-800 px-3 py-1 rounded-md text-xs font-medium">
                          Copied to clipboard!
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!secretKey && !merchantCode && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <p className="text-sm text-gray-500">
                      Click the "Reset Payment Keys" button below to generate
                      your keys.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Reset Button */}
          <div className="mb-6">
            <button
              onClick={handleResetKey}
              disabled={isResetting || isLoading}
              className={`
                w-full sm:w-auto min-w-[200px] px-8 py-4 rounded-lg font-semibold text-white 
                transition-all duration-200 transform
                ${
                  isResetting || isLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                }
                focus:outline-none focus:ring-4 focus:ring-red-200
                flex items-center justify-center
              `}
            >
              {isResetting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Generating Keys...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Reset Payment Keys
                </>
              )}
            </button>
          </div>

          {/* Status Message */}
          {message && (
            <div
              className={`mb-6 p-4 rounded-lg border ${
                message.includes("successfully")
                  ? "bg-green-50 text-green-800 border-green-200"
                  : "bg-red-50 text-red-800 border-red-200"
              }`}
            >
              <div className="flex items-start">
                <div
                  className={`flex-shrink-0 w-5 h-5 mt-0.5 ${
                    message.includes("successfully")
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {message.includes("successfully") ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <AlertTriangle className="w-5 h-5" />
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">{message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Warning Section */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="flex-shrink-0 w-5 h-5 text-amber-600 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-amber-800 mb-1">
                  Important Security Notice
                </h3>
                <p className="text-sm text-amber-700 leading-relaxed">
                  <strong>
                    Keys are only displayed once during generation.
                  </strong>{" "}
                  Make sure to copy and store your keys securely when they are
                  generated. Resetting payment keys will immediately invalidate
                  all existing keys.
                </p>
              </div>
            </div>
          </div>

          {/* Confirmation Modal */}
          {showConfirmModal && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              {/* Backdrop */}
              <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

              {/* Modal */}
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
                  {/* Modal Header */}
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Confirm Key Reset
                      </h3>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="px-6 py-4">
                    <p className="text-gray-700 mb-4">
                      Are you sure you want to reset your payment keys? This
                      action cannot be undone.
                    </p>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-start">
                        <AlertTriangle className="flex-shrink-0 w-4 h-4 text-red-600 mt-0.5" />
                        <div className="ml-2">
                          <p className="text-sm text-red-800 font-medium">
                            Warning:
                          </p>
                          <p className="text-sm text-red-700">
                            All existing keys will be invalidated immediately.
                            Make sure to update your applications with the new
                            keys.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:gap-0 sm:justify-end sm:space-x-3">
                    <button
                      onClick={cancelReset}
                      className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmReset}
                      className="w-full sm:w-auto px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-300"
                    >
                      Yes, Reset Keys
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Success Modal with Key */}
          {showSuccessModal && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              {/* Backdrop */}
              <div className="fixed inset-0 bg-black bg-opacity-70 transition-opacity"></div>

              {/* Modal */}
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 transform transition-all">
                  {/* Modal Header */}
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-green-50">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <ShieldAlert className="w-5 h-5 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Your New Payment Keys
                      </h3>
                    </div>
                    <button
                      onClick={closeSuccessModal}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="px-6 py-6">
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-800 font-bold">
                            IMPORTANT: ONE-TIME DISPLAY
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            These keys are displayed ONLY ONCE. Copy and store
                            them securely before closing this window. They will
                            NOT be shown again.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      {/* Merchant Code */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Merchant Code
                        </label>
                        <div className="relative">
                          <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm text-gray-900 break-all">
                            {merchantCode}
                          </div>
                          <button
                            onClick={async () => {
                              await navigator.clipboard.writeText(merchantCode);
                              // You could add a toast notification here instead of alert
                            }}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all duration-200"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Secret Key */}
                      <div className="space-y-2">
                        <label className="flex items-center justify-between">
                          <span className="block text-sm font-medium text-gray-700">
                            Secret Key
                          </span>
                          <span className="text-xs text-red-600 font-semibold">
                            COPY THIS NOW!
                          </span>
                        </label>
                        <div className="relative">
                          <div className="w-full px-4 py-3 bg-gray-50 border-2 border-red-300 rounded-lg font-mono text-sm text-gray-900 break-all">
                            {secretKey}
                          </div>
                          <button
                            onClick={copyToClipboard}
                            className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 ${
                              copySuccess
                                ? "bg-green-100 text-green-700"
                                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                            } rounded-md transition-all duration-200`}
                          >
                            {copySuccess ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
                      <div className="text-sm text-gray-500 flex items-center">
                        {keyCopied ? (
                          <span className="flex items-center text-green-600">
                            <Check className="w-4 h-4 mr-1" /> Key copied to
                            clipboard
                          </span>
                        ) : (
                          <span className="flex items-center text-amber-600">
                            <AlertTriangle className="w-4 h-4 mr-1" /> You
                            haven't copied your key yet!
                          </span>
                        )}
                      </div>
                      <button
                        onClick={closeSuccessModal}
                        className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 ${
                          keyCopied
                            ? "bg-green-600 hover:bg-green-700 text-white focus:ring-green-300"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-800 focus:ring-gray-300"
                        }`}
                      >
                        {keyCopied
                          ? "I've Saved My Keys"
                          : "Close Without Saving"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Close Warning Modal */}
          {showCloseWarningModal && (
            <div className="fixed inset-0 z-60 overflow-y-auto">
              {/* Backdrop */}
              <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"></div>

              {/* Modal */}
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
                  {/* Modal Header */}
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Warning: Key Not Saved!
                      </h3>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="px-6 py-4">
                    <p className="text-gray-700 mb-4 font-medium">
                      You haven't copied your secret key yet! This key will NOT
                      be shown again.
                    </p>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-start">
                        <AlertTriangle className="flex-shrink-0 w-4 h-4 text-red-600 mt-0.5" />
                        <div className="ml-2">
                          <p className="text-sm text-red-800 font-medium">
                            Critical Warning:
                          </p>
                          <p className="text-sm text-red-700">
                            If you close this window without copying your key,
                            you will lose access to it permanently and will need
                            to generate new keys.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:gap-0 sm:justify-end sm:space-x-3">
                    <button
                      onClick={cancelCloseWarning}
                      className="w-full sm:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-300"
                    >
                      Go Back & Copy Key
                    </button>
                    <button
                      onClick={forceCloseSuccessModal}
                      className="w-full sm:w-auto px-4 py-2 text-red-700 bg-red-100 hover:bg-red-200 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-300"
                    >
                      Close Anyway (Lose Key)
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentKeys;
